import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ListChecks, Car, Phone, Hash, ArrowUpRight, ArrowDownLeft, MapPin } from "lucide-react";
import { formatEUR, formatDateFR, photoUrl } from "../../lib/api";
import { useHoverCard, HoverShell, BookingHoverCard, VehicleHoverCard, BOOKING_STATUS } from "./HoverCards";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

const MAINT_TYPES_FR = {
  entretien: "Entretien courant", vidange: "Vidange", freins: "Freins", pneus: "Pneus",
  controle_technique: "Contrôle technique", reparation: "Réparation mécanique",
  carrosserie: "Carrosserie", nettoyage: "Nettoyage", depannage: "Dépannage", autre: "Autre",
};
const MAINT_STATUS_FR = { to_plan: "À planifier", planned: "Planifiée", in_progress: "En cours", done: "Terminée", cancelled: "Annulée" };

export default function PlanningView({ view, cursor, bookings, vehicles, blocks = [], onBookingStatusChange }) {
  const { hover, cardRef, openHover, scheduleHoverClose, cancelHoverClose, closeHover } = useHoverCard();
  const vehiclesById = useMemo(() => Object.fromEntries(vehicles.map((v) => [v.id, v])), [vehicles]);
  const today = ymd(new Date());

  const days = useMemo(() => {
    if (view === "day") return [new Date(cursor)];
    if (view === "week") {
      const r = new Date(cursor); const off = (r.getDay()+6)%7; r.setDate(r.getDate()-off);
      return Array.from({length:7}, (_,i) => { const d = new Date(r); d.setDate(d.getDate()+i); return d; });
    }
    if (view === "quarter") {
      const s = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const e = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 0);
      const n = Math.round((e - s) / 86400000) + 1;
      return Array.from({length:n}, (_,i) => { const d = new Date(s); d.setDate(d.getDate()+i); return d; });
    }
    const total = new Date(cursor.getFullYear(), cursor.getMonth()+1, 0).getDate();
    return Array.from({length:total}, (_,i) => new Date(cursor.getFullYear(), cursor.getMonth(), i+1));
  }, [view, cursor]);

  const byDay = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[ymd(d)] = []; });
    bookings.forEach((b) => {
      const s = new Date(b.start_date + "T00:00:00");
      const e = new Date(b.end_date + "T00:00:00");
      days.forEach((d) => {
        if (d >= s && d <= e) map[ymd(d)].push(b);
      });
    });
    return map;
  }, [days, bookings]);

  const blocksByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[ymd(d)] = []; });
    blocks.forEach((bl) => {
      const s = new Date(bl.start_date + "T00:00:00");
      const e = new Date(bl.end_date + "T00:00:00");
      days.forEach((d) => {
        if (d >= s && d <= e) map[ymd(d)].push(bl);
      });
    });
    return map;
  }, [days, blocks]);

  return (
    <div className="h-full overflow-y-auto p-4 sm:p-6" data-testid="pilotage-planning">
      <div className="w-full space-y-3">
        {days.map((d) => {
          const key = ymd(d);
          const dayBookings = byDay[key];
          const dayBlocks = blocksByDay[key];
          if (dayBookings.length === 0 && dayBlocks.length === 0) return null;
          const isToday = key === today;
          return (
            <div key={key} className={`rounded-2xl border bg-white p-4 ${isToday ? "border-violet-300 ring-1 ring-violet-100" : "border-slate-200"}`} data-testid={`planning-day-${key}`}>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                <div className="font-heading text-sm font-medium text-slate-900 capitalize">
                  {d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                </div>
                {isToday && <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[9px] font-bold uppercase tracking-wider">Aujourd'hui</span>}
                <div className="text-[10px] text-slate-400">
                  {dayBookings.length > 0 && `${dayBookings.length} réservation(s)`}
                  {dayBookings.length > 0 && dayBlocks.length > 0 && " · "}
                  {dayBlocks.length > 0 && `${dayBlocks.length} indisponibilité(s)`}
                </div>
              </div>
              {dayBlocks.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {dayBlocks.map((bl) => {
                    const v = vehiclesById[bl.vehicle_id];
                    const isMaint = !!bl.maintenance_id;
                    const conflict = dayBookings.some((b) => b.vehicle_id === bl.vehicle_id && ["confirmed", "pending"].includes(b.status));
                    const tooltip = isMaint
                      ? `${MAINT_TYPES_FR[bl.maintenance_type] || bl.maintenance_type || "Intervention"}${bl.provider ? ` · ${bl.provider}` : ""} · Blocage du ${formatDateFR(bl.start_date)} au ${formatDateFR(bl.end_date)} · ${MAINT_STATUS_FR[bl.maintenance_status] || bl.maintenance_status || ""}`
                      : `Indisponibilité manuelle du ${formatDateFR(bl.start_date)} au ${formatDateFR(bl.end_date)}${bl.reason ? ` · ${bl.reason}` : ""}`;
                    const inner = (
                      <>
                        <span className="text-sm">{isMaint ? "🔧" : "⛔"}</span>
                        <span className="text-xs font-bold">{isMaint ? "Garage" : "Bloqué"}</span>
                        {isMaint && bl.maintenance_ref && <span className="text-[10px] font-mono opacity-70">{bl.maintenance_ref}</span>}
                        <span className="text-xs font-medium truncate">{bl.vehicle_name || v?.name || "Véhicule"}</span>
                        <span className="text-[11px] opacity-80 truncate hidden sm:inline">
                          {isMaint ? (MAINT_TYPES_FR[bl.maintenance_type] || "Intervention") : (bl.reason || "Indisponibilité")}
                          {isMaint && bl.provider ? ` · ${bl.provider}` : ""}
                        </span>
                        <span className="ml-auto text-[10px] opacity-70 shrink-0">{formatDateFR(bl.start_date)} → {formatDateFR(bl.end_date)}</span>
                        {isMaint && (
                          <span className="px-1.5 py-0.5 rounded-full bg-white/60 text-[9px] font-bold shrink-0">{MAINT_STATUS_FR[bl.maintenance_status] || ""}</span>
                        )}
                        {conflict && (
                          <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[9px] font-bold shrink-0" data-testid={`planning-block-conflict-${bl.id}`}>⚠ Conflit résa</span>
                        )}
                      </>
                    );
                    const cls = `flex items-center gap-2 px-3 py-2 rounded-xl border w-full text-left ${
                      isMaint ? "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-colors" : "border-slate-200 bg-slate-50 text-slate-600"
                    }`;
                    return isMaint ? (
                      <Link
                        key={`bl-${bl.id}-${key}`}
                        to={`/vendor/pilotage?tab=vehicles&vehicle=${bl.vehicle_id}&vtab=maintenance`}
                        className={cls}
                        title={tooltip}
                        data-testid={`planning-block-${bl.id}`}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <div key={`bl-${bl.id}-${key}`} className={cls} title={tooltip} data-testid={`planning-block-${bl.id}`}>
                        {inner}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="space-y-2">
                {dayBookings.map((b) => {
                  const v = vehiclesById[b.vehicle_id];
                  const st = BOOKING_STATUS[b.status] || BOOKING_STATUS.confirmed;
                  const isDeparture = key === b.start_date;
                  const isReturn = key === b.end_date;
                  const nDays = Math.round((new Date(b.end_date + "T00:00:00") - new Date(b.start_date + "T00:00:00")) / 86400000) + 1;
                  return (
                    <div
                      key={`b-${b.id}-${key}`}
                      className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200 bg-white hover:border-violet-200 hover:shadow-sm transition-all"
                      onMouseEnter={(e) => openHover("booking", b, e, v)}
                      onMouseLeave={scheduleHoverClose}
                      data-testid={`planning-booking-${b.id}`}
                    >
                      <div className="h-11 w-16 rounded-lg overflow-hidden bg-slate-100 border border-slate-100 shrink-0 hidden sm:flex items-center justify-center">
                        {v?.photos?.[0] ? (
                          <img src={photoUrl(v.photos[0])} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <Car className="h-4 w-4 text-slate-300" />
                        )}
                      </div>
                      <span className={`h-2 w-2 rounded-full shrink-0 ${st.bg}`} />
                      <div className="w-40 lg:w-48 min-w-0 shrink-0">
                        <div className="text-xs font-semibold text-slate-900 truncate">{b.client_name}</div>
                        {b.client_phone && (
                          <div className="text-[10px] text-slate-500 flex items-center gap-1 truncate">
                            <Phone className="h-2.5 w-2.5 shrink-0" /> {b.client_phone}
                          </div>
                        )}
                      </div>
                      <div
                        className="w-40 lg:w-52 min-w-0 shrink-0 cursor-default"
                        onMouseEnter={(e) => { if (v) openHover("vehicle", v, e); }}
                        onMouseLeave={scheduleHoverClose}
                        data-testid={`planning-vehicle-${b.id}`}
                      >
                        <div className="text-xs font-medium text-slate-800 truncate underline decoration-dotted decoration-slate-300 underline-offset-2">{b.vehicle_name}</div>
                        {v?.license_plate && (
                          <div className="text-[10px] text-slate-400 font-mono flex items-center gap-1">
                            <Hash className="h-2.5 w-2.5 shrink-0" /> {v.license_plate}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 text-[11px] text-slate-500 hidden md:block">
                        <div className="truncate">
                          {formatDateFR(b.start_date)}{b.start_time ? ` ${b.start_time}` : ""} → {formatDateFR(b.end_date)}{b.end_time ? ` ${b.end_time}` : ""} · {nDays} j
                          {b.reference && <span className="ml-1.5 font-mono text-[10px] text-slate-400" data-testid={`planning-ref-${b.id}`}>{b.reference}</span>}
                        </div>
                        {(b.pickup_location_name || b.return_location_name) && (
                          <div className="text-[10px] text-slate-400 truncate flex items-center gap-1 mt-0.5" data-testid={`planning-agency-${b.id}`}>
                            <MapPin className="h-2.5 w-2.5 shrink-0" />
                            {!b.return_location_name || b.pickup_location_name === b.return_location_name
                              ? (b.pickup_location_name || b.return_location_name)
                              : `${b.pickup_location_name || "—"} → ${b.return_location_name}`}
                          </div>
                        )}
                      </div>
                      <div className="hidden lg:flex items-center gap-1.5 shrink-0">
                        {isDeparture && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold" data-testid={`planning-departure-${b.id}`}>
                            <ArrowUpRight className="h-2.5 w-2.5" /> Départ
                          </span>
                        )}
                        {isReturn && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold" data-testid={`planning-return-${b.id}`}>
                            <ArrowDownLeft className="h-2.5 w-2.5" /> Retour
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${st.bgLight} ${st.text}`}>{st.label}</span>
                      </div>
                      <div className="w-20 text-right text-xs font-semibold text-slate-900 shrink-0 hidden sm:block">{formatEUR(b.total)}</div>
                      <Link to={`/vendor/pilotage?tab=bookings&booking=${b.id}`} className="text-xs font-medium text-violet-600 hover:underline shrink-0" data-testid={`planning-booking-link-${b.id}`}>Détails →</Link>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {days.every((d) => byDay[ymd(d)].length === 0 && blocksByDay[ymd(d)].length === 0) && (
          <div className="py-16 text-center">
            <ListChecks className="h-8 w-8 mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">Aucune activité sur cette période.</div>
          </div>
        )}
      </div>

      {hover?.type === "booking" && (
        <HoverShell x={hover.x} y={hover.y} cardRef={cardRef} onMouseEnter={cancelHoverClose} onMouseLeave={scheduleHoverClose}>
          <BookingHoverCard booking={hover.item} vehicle={hover.extra} onStatusChange={onBookingStatusChange} onClose={closeHover} />
        </HoverShell>
      )}
      {hover?.type === "vehicle" && (
        <HoverShell x={hover.x} y={hover.y} cardRef={cardRef} onMouseEnter={cancelHoverClose} onMouseLeave={scheduleHoverClose}>
          <VehicleHoverCard vehicle={hover.item} onClose={closeHover} />
        </HoverShell>
      )}
    </div>
  );
}
